import React, { useState, useEffect } from "react";
import { API_URL } from "../../constant";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Line } from "react-chartjs-2";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

// X-axis labels
const labels = [
  "01",
  "02",
  "03",
  "04",
  "05",
  "06",
  "07",
  "08",
  "09",
  "10",
  "11",
  "12",
];

// Chart options generator
const generateOptions = (title, data) => {
  // Filter out null values for min/max calculation
  const validData = data.filter(value => value !== null);
  
  // If no valid data, use default values
  if (validData.length === 0) {
    return {
      responsive: true,
      scales: {
        y: {
          min: 0,
          max: 100,
          title: {
            display: true,
            text: "TopCoin UK Cash Value (£)",
            color: "#000",
          },
          ticks: {
            callback: (value) => "£" + value.toLocaleString(),
            color: "#000",
          },
        },
        x: {
          title: {
            display: true,
            text: "Time Period",
            color: "#000",
          },
          ticks: {
            color: "#000",
          },
        },
      },
      plugins: {
        legend: { position: "bottom" },
        title: { display: false, text: title },
      },
    };
  }
  
  // Calculate min and max from actual data
  const dataMax = Math.max(...validData);
  const dataMin = Math.min(...validData);
  
  // Add extra space above the maximum value (20% padding)
  const maxWithPadding = dataMax + (dataMax * 0.2);
  // Ensure min is at least 0 or lower with some padding
  const minWithPadding = Math.max(0, dataMin - (dataMin * 0.1));

  return {
    responsive: true,
    scales: {
      y: {
        min: minWithPadding,
        max: maxWithPadding,
        title: {
          display: true,
          text: "TopCoin UK Cash Value (£)",
          color: "#000",
        },
        ticks: {
          callback: (value) => "£" + value.toLocaleString(),
          color: "#000",
        },
      },
      x: {
        title: {
          display: true,
          text: "Time Period",
          color: "#000",
        },
        ticks: {
          color: "#000",
        },
      },
    },
    plugins: {
      legend: { position: "bottom" },
      title: { display: false, text: title },
    },
  };
};

// Chart data generator
const generateChartData = (datasets) => ({
  labels,
  datasets,
});

const ChartPoint = () => {
  const [cashData, setCashData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({});

  // Fetch chart data from backend
  const fetchCashData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch(`${API_URL}/api/public/wallet/chart-data`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch cash data');
      }
      
      const data = await response.json();
      
      // Use the cash value data from backend
      // Assuming your backend returns cumulative_cash_values array
      if (data.cumulative_cash_values) {
        setCashData(data.cumulative_cash_values);
      } else if (data.monthly_cash_values) {
        setCashData(data.monthly_cash_values);
      } else if (data.monthly_data) {
        // Extract cash values from monthly_data array
        const cashValues = data.monthly_data.map(month => month.cash_value);
        setCashData(cashValues);
      } else {
        // Fallback: try to calculate from points if cash values not directly available
        if (data.monthly_points && data.monthly_points.length > 0) {
          const conversionRate = data.overall_stats?.conversion_rate || 0.005;
          const cashValues = data.monthly_points.map(points => points * conversionRate);
          setCashData(cashValues);
        } else {
          setCashData([]);
        }
      }
      
      // Store stats for display if needed
      if (data.overall_stats) {
        setStats(data.overall_stats);
      }
      
    } catch (err) {
      console.error('Error fetching cash data:', err);
      setError(err.message);
      // Fallback to empty data if API fails
      setCashData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCashData();
    
    // Optional: Refresh data every 5 minutes
    const interval = setInterval(fetchCashData, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  // Handle loading and error states
  if (loading) {
    return (
      <div className="text-center py-4">
        <h4 className="text-theme">
          Graph illustrating the accumulation of TopCoins Current Cash Value
        </h4>
        <div className="text-center py-5">
          <div className="spinner-border text-warning" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2">Loading cash value data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-4">
        <h4 className="text-theme">
          Graph illustrating the accumulation of TopCoins Current Cash Value
        </h4>
        <div className="alert alert-warning mt-3">
          <strong>Warning:</strong> {error}
        </div>
        <button 
          className="btn btn-outline-warning mt-2"
          onClick={fetchCashData}
        >
          Retry Loading Data
        </button>
      </div>
    );
  }

  // Use actual data or fallback to zeros if no data
  const chartData = cashData.length > 0 ? cashData : Array(12).fill(0);

  return (
    <>
      <div className="text-center py-4">
        <h4 className="text-theme">
          Graph showing how TopCoin Promotional Reward Allocation is displayed over time
        </h4>
        {stats.total_cash_value && (
          <div className="mt-2">
            <small className="text-muted">
              Current Total Cash Value: £{stats.total_cash_value?.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              {stats.conversion_rate && (
                <span className="ms-2">(Conversion Rate: £{stats.conversion_rate} per point)</span>
              )}
            </small>
          </div>
        )}
      </div>
      <div
        className="chart-wrapper"
        style={{ backgroundColor: "#F8DB04", padding: "20px" }}
      >
        <Line
          data={generateChartData([
            {
              label: "TopCoin Value",
              data: chartData,
              borderColor: "rgba(0,0,103,1)",
              backgroundColor: "rgba(0,0,103,0.1)",
              tension: 0.4,
              fill: true,
            },
          ])}
          options={generateOptions("Time Period", chartData)}
        />
      </div>
    </>
  );
};

export default ChartPoint;